/**
 * RightPseudoBox Component
 *
 * SVG component for displaying player pseudonyms on the right side of the game board.
 * Features a football field aesthetic with grass texture, brown border, and decorative elements.
 * Displays player username in white text with fixed font size and ellipsis truncation for long names.
 *
 * Visual reference: planning/visuals/pseudo-right.svg - Right pseudo box design
 */

import type { PseudoBoxProps } from './constants/types';
import { buildClassNames } from '@/utils/classNames';
import { formatPseudoForDisplay, PSEUDO_BOX_CONFIG } from './utils/utils';

export default function RightPseudoBox({
  pseudo,
  viewBox = PSEUDO_BOX_CONFIG.DEFAULT_VIEWBOX,
  className = ''
}: PseudoBoxProps) {
  const svgClassName = buildClassNames(className);
  const displayText = formatPseudoForDisplay(pseudo);

  return (
    <svg
      viewBox={viewBox}
      className={svgClassName}
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Player username display box - right position"
      focusable="false"
    >
      <defs>
        <linearGradient
          id="rightPseudoGradient1"
          x1="153.11"
          y1="78.0298"
          x2="153.11"
          y2="4.09983"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#96C23B" />
          <stop offset="1" stopColor="#BBD849" />
        </linearGradient>
        {/* Clip path to prevent text overflow outside the box */}
        <clipPath id="rightPseudoTextClip">
          <rect x="15" y="5" width="277" height="75" rx="20" />
        </clipPath>
      </defs>

      {/* Brown base layer - muddy field edge */}
      <path
        d="M4.48997 48.7299V74.16C4.48997 81.6 8.46997 88.5499 15.03 92.07C15.08 92.0999 15.13 92.13 15.19 92.15C22.79 96.17 29.96 95.2399 38.63 94.8399C49.19 94.3499 59.76 95.9 70.33 95.9C91.4 95.9 112.47 95.93 133.54 95.9C148.69 95.88 163.61 95.9 174.27 95.9C190.61 95.91 198.78 95.92 199.63 95.94C235.53 96.5 238.39 98.93 251.12 95.94C259.92 93.87 263.07 91.6499 271.29 91.7699C279.08 91.8799 284.07 93.9799 290.5 91.3899C295.76 89.27 299.53 84.2799 299.53 84.2799C299.54 84.27 299.55 84.2599 299.56 84.2399C301.09 82.4699 301.89 80.1799 301.87 77.8399C301.76 66.7899 301.66 55.74 301.55 44.7C254.07 51.83 198.94 56.94 137.59 56.9C88.81 56.87 44.17 53.5999 4.46997 48.7299H4.48997Z"
        fill="#72381B"
      />

      {/* Dark green depth layer */}
      <path
        d="M4.48997 39.0801V72.64C6.52997 75.66 9.90997 79.7201 15.19 82.5C22.79 86.5201 29.96 85.5901 38.63 85.1901C49.19 84.7001 59.76 86.25 70.33 86.25C91.4 86.25 112.47 86.28 133.54 86.25C148.69 86.23 163.61 86.25 174.27 86.25C190.61 86.26 198.78 86.2701 199.63 86.2901C235.53 86.8501 238.39 89.2801 251.12 86.2901C259.92 84.2201 263.07 82.0001 271.29 82.1201C279.08 82.2301 284.07 84.33 290.5 81.74C295.76 79.62 299.53 74.63 299.53 74.63C300.59 73.41 301.37 72.3 301.9 71.49C301.78 59.34 301.67 47.19 301.55 35.05C254.07 42.18 198.94 47.29 137.59 47.25C88.81 47.22 44.17 43.9501 4.46997 39.0801H4.48997Z"
        fill="#4B9E36"
      />

      {/* Light green gradient surface - main box */}
      <path
        d="M4.48999 43.6998L4.48999 28.5998C4.48999 14.9898 15.54 3.93983 29.15 3.93983L277.06 3.93983C290.67 3.93983 301.72 14.9898 301.72 28.5998V54.9198C301.72 68.5298 290.67 79.5798 277.06 79.5798L40.37 79.5798C20.57 79.5798 4.48999 63.4998 4.48999 43.6998Z"
        fill="url(#rightPseudoGradient1)"
      />

      {/* Decorative grass blade elements */}
      <path
        d="M61.185 16.477L60.4955 19.2526C60.4489 19.4402 60.5632 19.6301 60.7508 19.6767C60.9384 19.7233 61.1283 19.609 61.1749 19.4214L61.8643 16.6457C61.9109 16.4581 61.7966 16.2683 61.609 16.2217C61.4214 16.1751 61.2316 16.2894 61.185 16.477Z"
        fill="#69AA3F"
      />
      <path
        d="M59.51 16.25V18.63C59.51 18.8233 59.6667 18.98 59.86 18.98C60.0533 18.98 60.21 18.8233 60.21 18.63V16.25C60.21 16.0567 60.0533 15.9 59.86 15.9C59.6667 15.9 59.51 16.0567 59.51 16.25Z"
        fill="#69AA3F"
      />
      <path
        d="M58.1204 17.5184L58.7199 19.8217C58.7686 20.0088 58.9598 20.1209 59.1468 20.0722C59.3339 20.0235 59.4461 19.8324 59.3974 19.6454L58.7978 17.3421C58.7492 17.155 58.558 17.0429 58.371 17.0916C58.1839 17.1402 58.0717 17.3314 58.1204 17.5184Z"
        fill="#69AA3F"
      />
      <path
        d="M82.9968 50.1618L82.3073 52.9374C82.2607 53.125 82.375 53.3149 82.5626 53.3615C82.7502 53.4081 82.94 53.2938 82.9866 53.1062L83.6761 50.3305C83.7227 50.1429 83.6084 49.9531 83.4208 49.9065C83.2332 49.8599 83.0434 49.9742 82.9968 50.1618Z"
        fill="#69AA3F"
      />
      <path
        d="M81.3101 49.9299V52.3099C81.3101 52.5032 81.4668 52.6599 81.6601 52.6599C81.8534 52.6599 82.0101 52.5032 82.0101 52.3099V49.9299C82.0101 49.7366 81.8534 49.5799 81.6601 49.5799C81.4668 49.5799 81.3101 49.7366 81.3101 49.9299Z"
        fill="#69AA3F"
      />
      <path
        d="M79.9129 51.2074L80.5124 53.5106C80.5611 53.6977 80.7522 53.8099 80.9393 53.7612C81.1264 53.7125 81.2385 53.5214 81.1898 53.3343L80.5903 51.0311C80.5416 50.844 80.3505 50.7318 80.1634 50.7805C79.9764 50.8292 79.8642 51.0203 79.9129 51.2074Z"
        fill="#69AA3F"
      />
      <path
        d="M117.06 23.0385L116.371 25.8141C116.324 26.0017 116.438 26.1916 116.626 26.2382C116.813 26.2848 117.003 26.1705 117.05 25.9829L117.739 23.2072C117.786 23.0196 117.672 22.8298 117.484 22.7832C117.296 22.7366 117.107 22.8509 117.06 23.0385Z"
        fill="#69AA3F"
      />
      <path
        d="M115.38 22.8198V25.1998C115.38 25.3931 115.537 25.5498 115.73 25.5498C115.923 25.5498 116.08 25.3931 116.08 25.1998V22.8198C116.08 22.6265 115.923 22.4698 115.73 22.4698C115.537 22.4698 115.38 22.6265 115.38 22.8198Z"
        fill="#69AA3F"
      />
      <path
        d="M113.978 24.0971L114.577 26.4003C114.626 26.5874 114.817 26.6995 115.004 26.6508C115.191 26.6022 115.303 26.411 115.255 26.224L114.655 23.9207C114.606 23.7337 114.415 23.6215 114.228 23.6702C114.041 23.7189 113.929 23.91 113.978 24.0971Z"
        fill="#69AA3F"
      />
      <path
        d="M153.359 58.2846L152.67 61.0602C152.623 61.2478 152.737 61.4377 152.925 61.4843C153.112 61.5309 153.302 61.4166 153.349 61.229L154.038 58.4533C154.085 58.2657 153.971 58.0759 153.783 58.0293C153.596 57.9827 153.406 58.097 153.359 58.2846Z"
        fill="#69AA3F"
      />
      <path
        d="M151.67 58.05V60.43C151.67 60.6233 151.827 60.78 152.02 60.78C152.213 60.78 152.37 60.6233 152.37 60.43V58.05C152.37 57.8567 152.213 57.7 152.02 57.7C151.827 57.7 151.67 57.8567 151.67 58.05Z"
        fill="#69AA3F"
      />
      <path
        d="M150.267 59.3209L150.867 61.6242C150.915 61.8113 151.106 61.9234 151.294 61.8747C151.481 61.826 151.593 61.6349 151.544 61.4479L150.945 59.1446C150.896 58.9575 150.705 58.8454 150.518 58.894C150.331 58.9427 150.218 59.1339 150.267 59.3209Z"
        fill="#69AA3F"
      />
      <path
        d="M187.445 68.8976L186.755 71.6733C186.708 71.8609 186.823 72.0507 187.01 72.0973C187.198 72.1439 187.388 72.0296 187.434 71.842L188.124 69.0664C188.17 68.8788 188.056 68.6889 187.869 68.6423C187.681 68.5957 187.491 68.71 187.445 68.8976Z"
        fill="#69AA3F"
      />
      <path
        d="M185.76 68.6599V71.0399C185.76 71.2332 185.917 71.3899 186.11 71.3899C186.303 71.3899 186.46 71.2332 186.46 71.0399V68.6599C186.46 68.4666 186.303 68.3099 186.11 68.3099C185.917 68.3099 185.76 68.4666 185.76 68.6599Z"
        fill="#69AA3F"
      />
      <path
        d="M184.357 69.9349L184.957 72.2382C185.006 72.4253 185.197 72.5374 185.384 72.4887C185.571 72.44 185.683 72.2489 185.634 72.0619L185.035 69.7586C184.986 69.5715 184.795 69.4594 184.608 69.5081C184.421 69.5568 184.309 69.7479 184.357 69.9349Z"
        fill="#69AA3F"
      />
      <path
        d="M208.768 26.8246L208.078 29.6003C208.031 29.7879 208.146 29.9777 208.333 30.0243C208.521 30.0709 208.711 29.9566 208.757 29.769L209.447 26.9934C209.493 26.8058 209.379 26.6159 209.192 26.5693C209.004 26.5227 208.814 26.637 208.768 26.8246Z"
        fill="#69AA3F"
      />
      <path
        d="M207.09 26.5898V28.9698C207.09 29.1631 207.247 29.3198 207.44 29.3198C207.633 29.3198 207.79 29.1631 207.79 28.9698V26.5898C207.79 26.3965 207.633 26.2398 207.44 26.2398C207.247 26.2398 207.09 26.3965 207.09 26.5898Z"
        fill="#69AA3F"
      />
      <path
        d="M205.684 27.8798L206.283 30.183C206.332 30.3701 206.523 30.4823 206.71 30.4336C206.897 30.3849 207.009 30.1937 206.961 30.0067L206.361 27.7034C206.312 27.5164 206.121 27.4042 205.934 27.4529C205.747 27.5016 205.635 27.6927 205.684 27.8798Z"
        fill="#69AA3F"
      />
      <path
        d="M232.618 65.0146L231.929 67.7902C231.882 67.9778 231.997 68.1677 232.184 68.2143C232.372 68.2609 232.562 68.1466 232.608 67.959L233.298 65.1833C233.344 64.9957 233.23 64.8059 233.042 64.7593C232.855 64.7127 232.665 64.827 232.618 65.0146Z"
        fill="#69AA3F"
      />
      <path
        d="M230.93 64.78V67.16C230.93 67.3533 231.087 67.51 231.28 67.51C231.473 67.51 231.63 67.3533 231.63 67.16V64.78C231.63 64.5867 231.473 64.43 231.28 64.43C231.087 64.43 230.93 64.5867 230.93 64.78Z"
        fill="#69AA3F"
      />
      <path
        d="M229.525 66.0514L230.124 68.3546C230.173 68.5417 230.364 68.6539 230.551 68.6052C230.738 68.5565 230.85 68.3654 230.802 68.1783L230.202 65.8751C230.153 65.688 229.962 65.5758 229.775 65.6245C229.588 65.6732 229.476 65.8643 229.525 66.0514Z"
        fill="#69AA3F"
      />
      <path
        d="M69.3903 66.6979L68.7008 69.4736C68.6542 69.6612 68.7685 69.851 68.9561 69.8976C69.1437 69.9442 69.3336 69.8299 69.3802 69.6423L70.0697 66.8667C70.1163 66.6791 70.002 66.4892 69.8144 66.4426C69.6268 66.396 69.4369 66.5103 69.3903 66.6979Z"
        fill="#69AA3F"
      />
      <path
        d="M67.71 66.4699V68.8499C67.71 69.0432 67.8667 69.2 68.06 69.2C68.2533 69.2 68.41 69.0432 68.41 68.8499V66.4699C68.41 66.2766 68.2533 66.1199 68.06 66.1199C67.8667 66.1199 67.71 66.2766 67.71 66.4699Z"
        fill="#69AA3F"
      />
      <path
        d="M66.3079 67.7521L66.9074 70.0553C66.9561 70.2424 67.1473 70.3546 67.3343 70.3059C67.5214 70.2572 67.6336 70.0661 67.5849 69.879L66.9853 67.5757C66.9367 67.3887 66.7455 67.2765 66.5585 67.3252C66.3714 67.3739 66.2592 67.565 66.3079 67.7521Z"
        fill="#69AA3F"
      />
      <path
        d="M95.7516 11.2082L95.0622 13.9838C95.0156 14.1714 95.1299 14.3613 95.3175 14.4079C95.5051 14.4545 95.6949 14.3402 95.7415 14.1526L96.431 11.3769C96.4776 11.1893 96.3633 10.9995 96.1757 10.9529C95.9881 10.9063 95.7982 11.0206 95.7516 11.2082Z"
        fill="#69AA3F"
      />
      <path
        d="M94.0698 10.99V13.37C94.0698 13.5633 94.2265 13.72 94.4198 13.72C94.6131 13.72 94.7698 13.5633 94.7698 13.37V10.99C94.7698 10.7967 94.6131 10.64 94.4198 10.64C94.2265 10.64 94.0698 10.7967 94.0698 10.99Z"
        fill="#69AA3F"
      />
      <path
        d="M92.6746 12.2604L93.2741 14.5636C93.3228 14.7507 93.514 14.8629 93.701 14.8142C93.8881 14.7655 94.0003 14.5744 93.9516 14.3873L93.352 12.084C93.3034 11.897 93.1122 11.7848 92.9252 11.8335C92.7381 11.8822 92.6259 12.0733 92.6746 12.2604Z"
        fill="#69AA3F"
      />
      <path
        d="M173.512 14.3854L172.822 17.1611C172.776 17.3487 172.89 17.5385 173.077 17.5851C173.265 17.6317 173.455 17.5174 173.502 17.3298L174.191 14.5542C174.238 14.3666 174.123 14.1767 173.936 14.1301C173.748 14.0835 173.558 14.1978 173.512 14.3854Z"
        fill="#69AA3F"
      />
      <path
        d="M171.82 14.1499V16.5299C171.82 16.7232 171.977 16.8799 172.17 16.8799C172.363 16.8799 172.52 16.7232 172.52 16.5299V14.1499C172.52 13.9566 172.363 13.7999 172.17 13.7999C171.977 13.7999 171.82 13.9566 171.82 14.1499Z"
        fill="#69AA3F"
      />
      <path
        d="M170.417 15.4291L171.017 17.7323C171.065 17.9194 171.256 18.0316 171.443 17.9829C171.631 17.9342 171.743 17.7431 171.694 17.556L171.094 15.2528C171.046 15.0657 170.855 14.9535 170.668 15.0022C170.481 15.0509 170.368 15.242 170.417 15.4291Z"
        fill="#69AA3F"
      />
      <path
        d="M126.576 64.965L125.887 67.7406C125.84 67.9282 125.954 68.1181 126.142 68.1647C126.33 68.2113 126.519 68.097 126.566 67.9094L127.255 65.1337C127.302 64.9462 127.188 64.7563 127 64.7097C126.813 64.6631 126.623 64.7774 126.576 64.965Z"
        fill="#69AA3F"
      />
      <path
        d="M124.89 64.74V67.12C124.89 67.3133 125.046 67.47 125.24 67.47C125.433 67.47 125.59 67.3133 125.59 67.12V64.74C125.59 64.5467 125.433 64.39 125.24 64.39C125.046 64.39 124.89 64.5467 124.89 64.74Z"
        fill="#69AA3F"
      />
      <path
        d="M123.488 66.0045L124.087 68.3078C124.136 68.4948 124.327 68.607 124.514 68.5583C124.701 68.5096 124.813 68.3185 124.765 68.1314L124.165 65.8282C124.116 65.6411 123.925 65.5289 123.738 65.5776C123.551 65.6263 123.439 65.8174 123.488 66.0045Z"
        fill="#69AA3F"
      />
      <path
        d="M26.0962 60.0117L25.5538 62.1953C25.5165 62.3454 25.6058 62.4967 25.7532 62.5333C25.9006 62.57 26.0503 62.478 26.0876 62.3279L26.63 60.1443C26.6673 59.9942 26.578 59.8428 26.4306 59.8062C26.2832 59.7696 26.1335 59.8616 26.0962 60.0117Z"
        fill="#69AA3F"
      />
      <path
        d="M24.78 59.8397V61.7097C24.78 61.8644 24.9032 61.9897 25.055 61.9897C25.2069 61.9897 25.33 61.8644 25.33 61.7097V59.8397C25.33 59.6851 25.2069 59.5597 25.055 59.5597C24.9032 59.5597 24.78 59.6851 24.78 59.8397Z"
        fill="#69AA3F"
      />
      <path
        d="M23.6747 60.8429L24.1458 62.6526C24.1847 62.8023 24.3355 62.8926 24.4824 62.8543C24.6294 62.8161 24.717 62.6637 24.678 62.5141L24.207 60.7044C24.168 60.5547 24.0173 60.4644 23.8703 60.5027C23.7233 60.5409 23.6358 60.6933 23.6747 60.8429Z"
        fill="#69AA3F"
      />
      <path
        d="M43.859 41.6933L43.1093 44.7116C43.0627 44.8992 43.177 45.089 43.3646 45.1356L43.4131 45.1477C43.6007 45.1943 43.7906 45.08 43.8372 44.8924L44.5869 41.8741C44.6335 41.6865 44.5192 41.4967 44.3316 41.4501L44.2831 41.438C44.0955 41.3914 43.9056 41.5057 43.859 41.6933Z"
        fill="#69AA3F"
      />
      <path
        d="M42.0498 41.4599V44.0499C42.0498 44.2432 42.2065 44.3999 42.3998 44.3999H42.4498C42.6431 44.3999 42.7998 44.2432 42.7998 44.0499V41.4599C42.7998 41.2666 42.6431 41.1099 42.4498 41.1099H42.3998C42.2065 41.1099 42.0498 41.2666 42.0498 41.4599Z"
        fill="#69AA3F"
      />
      <path
        d="M40.5556 42.8091L41.208 45.3156C41.2567 45.5027 41.4478 45.6148 41.6349 45.5661L41.6832 45.5535C41.8703 45.5048 41.9825 45.3137 41.9338 45.1267L41.2814 42.6202C41.2327 42.4331 41.0416 42.3209 40.8545 42.3696L40.8061 42.3822C40.619 42.4309 40.5069 42.622 40.5556 42.8091Z"
        fill="#69AA3F"
      />
      <path
        d="M35.7042 10.1518L34.9545 13.1701C34.9079 13.3577 35.0222 13.5475 35.2098 13.5941L35.2583 13.6062C35.4459 13.6528 35.6358 13.5385 35.6824 13.3509L36.4321 10.3326C36.4787 10.145 36.3644 9.95517 36.1768 9.90857L36.1283 9.89651C35.9407 9.84991 35.7508 9.96422 35.7042 10.1518Z"
        fill="#69AA3F"
      />
      <path
        d="M33.8999 9.92987V12.5199C33.8999 12.7132 34.0566 12.8699 34.2499 12.8699H34.2999C34.4932 12.8699 34.6499 12.7132 34.6499 12.5199V9.92987C34.6499 9.73657 34.4932 9.57988 34.2999 9.57988H34.2499C34.0566 9.57988 33.8999 9.73657 33.8999 9.92987Z"
        fill="#69AA3F"
      />
      <path
        d="M32.4079 11.2884L33.0603 13.7948C33.109 13.9819 33.3001 14.0941 33.4872 14.0454L33.5355 14.0328C33.7226 13.9841 33.8348 13.793 33.7861 13.6059L33.1337 11.0994C33.085 10.9124 32.8939 10.8002 32.7068 10.8489L32.6584 10.8615C32.4713 10.9102 32.3592 11.1013 32.4079 11.2884Z"
        fill="#69AA3F"
      />
      <path
        d="M17.6985 29.2361L17.1537 31.4294C17.1164 31.5795 17.2057 31.7309 17.3531 31.7675C17.5005 31.8041 17.6502 31.7121 17.6875 31.562L18.2323 29.3687C18.2696 29.2186 18.1803 29.0673 18.0329 29.0306C17.8855 28.994 17.7358 29.086 17.6985 29.2361Z"
        fill="#69AA3F"
      />
      <path
        d="M16.3699 29.0497V30.9297C16.3699 31.0844 16.493 31.2097 16.6449 31.2097C16.7968 31.2097 16.9199 31.0844 16.9199 30.9297V29.0497C16.9199 28.8951 16.7968 28.7697 16.6449 28.7697C16.493 28.7697 16.3699 28.8951 16.3699 29.0497Z"
        fill="#69AA3F"
      />
      <path
        d="M15.263 30.073L15.7366 31.8924C15.7756 32.042 15.9263 32.1323 16.0733 32.0941C16.2202 32.0558 16.3078 31.9035 16.2689 31.7538L15.7953 29.9344C15.7563 29.7848 15.6056 29.6945 15.4586 29.7327C15.3116 29.771 15.2241 29.9233 15.263 30.073Z"
        fill="#69AA3F"
      />
      <path
        d="M196.252 15.6856L195.707 17.8789C195.67 18.029 195.759 18.1803 195.907 18.2169C196.054 18.2535 196.204 18.1616 196.241 18.0115L196.786 15.8181C196.823 15.6681 196.734 15.5167 196.586 15.4801C196.439 15.4435 196.289 15.5355 196.252 15.6856Z"
        fill="#69AA3F"
      />
      <path
        d="M194.92 15.4997V17.3797C194.92 17.5343 195.043 17.6597 195.195 17.6597C195.347 17.6597 195.47 17.5343 195.47 17.3797V15.4997C195.47 15.345 195.347 15.2197 195.195 15.2197C195.043 15.2197 194.92 15.345 194.92 15.4997Z"
        fill="#69AA3F"
      />
      <path
        d="M193.803 16.5039L194.277 18.3233C194.316 18.4729 194.466 18.5632 194.613 18.525C194.76 18.4867 194.848 18.3344 194.809 18.1847L194.335 16.3654C194.296 16.2157 194.146 16.1254 193.999 16.1637C193.852 16.2019 193.764 16.3542 193.803 16.5039Z"
        fill="#69AA3F"
      />
      <path
        d="M249.33 61.6753L248.785 63.8686C248.748 64.0187 248.837 64.1701 248.984 64.2067C249.132 64.2433 249.282 64.1513 249.319 64.0012L249.864 61.8079C249.901 61.6578 249.812 61.5065 249.664 61.4698C249.517 61.4332 249.367 61.5252 249.33 61.6753Z"
        fill="#69AA3F"
      />
      <path
        d="M247.99 61.4796V63.3596C247.99 63.5143 248.113 63.6396 248.265 63.6396C248.417 63.6396 248.54 63.5143 248.54 63.3596V61.4796C248.54 61.325 248.417 61.1996 248.265 61.1996C248.113 61.1996 247.99 61.325 247.99 61.4796Z"
        fill="#69AA3F"
      />
      <path
        d="M246.872 62.4927L247.346 64.312C247.385 64.4617 247.535 64.552 247.682 64.5137C247.829 64.4755 247.917 64.3231 247.878 64.1735L247.404 62.3541C247.365 62.2045 247.215 62.1142 247.068 62.1524C246.921 62.1907 246.833 62.343 246.872 62.4927Z"
        fill="#69AA3F"
      />
      <path
        d="M294.939 43.9214L294.394 46.1147C294.357 46.2648 294.446 46.4162 294.594 46.4528C294.741 46.4894 294.891 46.3974 294.928 46.2473L295.473 44.054C295.51 43.9039 295.421 43.7526 295.273 43.716C295.126 43.6794 294.976 43.7713 294.939 43.9214Z"
        fill="#69AA3F"
      />
      <path
        d="M293.59 43.7397V45.6197C293.59 45.7743 293.713 45.8997 293.865 45.8997C294.017 45.8997 294.14 45.7743 294.14 45.6197V43.7397C294.14 43.585 294.017 43.4597 293.865 43.4597C293.713 43.4597 293.59 43.585 293.59 43.7397Z"
        fill="#69AA3F"
      />
      <path
        d="M292.475 44.7449L292.949 46.5642C292.988 46.7139 293.139 46.8042 293.286 46.7659C293.433 46.7277 293.52 46.5753 293.481 46.4257L293.008 44.6063C292.969 44.4567 292.818 44.3664 292.671 44.4046C292.524 44.4429 292.436 44.5952 292.475 44.7449Z"
        fill="#69AA3F"
      />
      <path
        d="M265.384 10.9788L264.839 13.1721C264.802 13.3222 264.891 13.4735 265.039 13.5101C265.186 13.5468 265.336 13.4548 265.373 13.3047L265.918 11.1114C265.955 10.9613 265.866 10.81 265.719 10.7733C265.571 10.7367 265.422 10.8287 265.384 10.9788Z"
        fill="#69AA3F"
      />
      <path
        d="M264.04 10.7897V12.6697C264.04 12.8243 264.163 12.9497 264.315 12.9497C264.467 12.9497 264.59 12.8243 264.59 12.6697V10.7897C264.59 10.6351 264.467 10.5097 264.315 10.5097C264.163 10.5097 264.04 10.6351 264.04 10.7897Z"
        fill="#69AA3F"
      />
      <path
        d="M262.924 11.8091L263.397 13.6284C263.436 13.7781 263.587 13.8684 263.734 13.8301C263.881 13.7919 263.968 13.6396 263.93 13.4899L263.456 11.6705C263.417 11.5209 263.266 11.4306 263.119 11.4688C262.972 11.5071 262.885 11.6594 262.924 11.8091Z"
        fill="#69AA3F"
      />
      <path
        d="M101.142 63.1914L100.598 65.3848C100.56 65.5348 100.65 65.6862 100.797 65.7228C100.944 65.7594 101.094 65.6674 101.131 65.5173L101.676 63.324C101.713 63.1739 101.624 63.0226 101.477 62.986C101.329 62.9493 101.18 63.0413 101.142 63.1914Z"
        fill="#69AA3F"
      />
      <path
        d="M99.8098 62.9997V64.8797C99.8098 65.0343 99.9329 65.1597 100.085 65.1597C100.237 65.1597 100.36 65.0343 100.36 64.8797V62.9997C100.36 62.845 100.237 62.7197 100.085 62.7197C99.9329 62.7197 99.8098 62.845 99.8098 62.9997Z"
        fill="#69AA3F"
      />
      <path
        d="M98.6995 64.0115L99.1731 65.8308C99.2121 65.9805 99.3628 66.0708 99.5098 66.0325C99.6568 65.9943 99.7443 65.8419 99.7054 65.6923L99.2318 63.8729C99.1929 63.7233 99.0421 63.633 98.8951 63.6712C98.7482 63.7095 98.6606 63.8618 98.6995 64.0115Z"
        fill="#69AA3F"
      />

      {/* Dynamic player pseudo text - clipped to prevent overflow */}
      <g clipPath="url(#rightPseudoTextClip)">
        <text
          x={PSEUDO_BOX_CONFIG.TEXT.RIGHT_X_POSITION}
          y={PSEUDO_BOX_CONFIG.TEXT.Y_POSITION}
          fill="#FFFFFF"
          fontSize={PSEUDO_BOX_CONFIG.TEXT.FONT_SIZE}
          fontWeight="bold"
          textAnchor="end"
          dominantBaseline="middle"
          stroke="#000000"
          strokeWidth="2"
          strokeLinejoin="round"
          paintOrder="stroke"
        >
          {displayText}
        </text>
      </g>
    </svg>
  );
}
